<template>
  <div
    class="cart-samples"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <span
      class="cart-samples__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      v-text="$string('cart.samples.title')"
    />
    <div
      v-if="filteredProducts.length"
      ref="carousel"
      class="cart-samples__splide splide"
    >
      <div class="splide__track carousel">
        <div class="cart-samples__list carousel splide__list">
          <div
            v-for="product of filteredProducts"
            :key="`${product.handle}-${product.selectedOrFirstAvailableVariant?.id}`"
            class="splide__slide"
          >
            <product-card-sample
              class="cart-samples__product"
              :product="product"
              :variant="product.selectedOrFirstAvailableVariant"
              :disabled="getCartItemLimitReached"
              @emit-error="handleError"
            />
          </div>
        </div>
      </div>

      <div class="splide__arrows">
        <button class="splide__arrow splide__arrow--prev" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button class="splide__arrow splide__arrow--next" type="button">
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>
    </div>

    <div v-else>
      <p
        class="cart-samples__empty text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="$string('cart.samples.empty')"
      />
    </div>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
</template>

<script>
/**
 * Vue: Cart samples (cart-samples)
 * -----------------------------------------------------------------------------
 * Shows a set of products selected from the customizer in the drawer cart.
 *
 * @param {Object} overlay - Overlay API.
 * @param {Array} samples - Array of sample products to load.
 *
 */
import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';

import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import ProductCardSample from '~async/product-card-sample/product-card-sample';

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';

import nodesByIdQuery from '~graphql/nodes-by-id-product-inline.gql';
import { formatProduct } from '~/format/format-product';
import { transformId } from '~/helpers/graphql';
import timings from '~/config/timings';
import useCartStore from '~stores/cart';
import useIndexStore from '~stores/index';

export default {
  name: 'CartSamples',

  components: {
    NotificationPanel,
    ProductCardSample,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
    samples: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      carousel: null,
      error: '',
      filteredProducts: [false, false, false],
      previousCartItemCount: 0,
      products: [],
      state: 'loading',
      timeout: {},
    };
  },

  computed: {
    ...mapState(useCartStore, {
      getCartItemLimitReached: 'getCartItemLimitReached',
      cart: 'response',
    }),

    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
  },

  watch: {
    /**
     * Watch cart items to update filtered products.
     */
    'cart.items'() {
      let timing = this.$timing('addedToCart');

      /**
       * If less items in cart than previously then instantly update.
       */
      if (this.cart.item_count < this.previousCartItemCount) {
        timing = 0;
      }

      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        this.filterProducts();
        this.previousCartItemCount = this.cart.item_count;
        this.refreshCarousel();
      }, timing);
    },

    async isMobile() {
      await this.$nextTick();
      this.handleResize();
    },
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    /**
     * Construct the carousel.
     */
    constructCarousel() {
      if (!this.$refs.carousel) {
        return;
      }

      this.carousel = new Splide(this.$refs.carousel, {
        arrows: !this.isMobile,
        drag: false,
        focus: false,
        gap: 'var(--layout-gutter)',
        mediaQuery: 'min',
        omitEnd: true,
        pagination: false,
        perMove: 1,
        perPage: 1.7,
        slideFocus: false,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
      });

      setTimeout(() => {
        this.carousel.mount();
      }, timings.normal);
    },

    /**
     * Refresh carousel. Will be triggered when filtered products change
     */
    async refreshCarousel() {
      if (this.carousel) {
        await this.$nextTick();
        this.carousel.refresh();
      }
    },

    /**
     * Fetch data.
     */
    async fetchData() {
      try {
        const ids = this.samples.map(({ id }) => transformId(id, 'Product'));

        if (!ids) {
          return;
        }

        const response = await cnvs.Query({
          query: nodesByIdQuery,
          variables: {
            country: this.$variable('store.country'),
            ids,
            language: this.$variable('store.language'),
          },
        });

        if (this.$variable('settings.disableComponentReadyState')) {
          return;
        }

        this.products = response.nodes
          .map((product, index) => {
            if (!product) {
              return false;
            }

            let variantId = false;

            if (this.samples[index]) {
              variantId = this.samples[index].variantId;
            }

            return formatProduct(product, { variantId });
          })
          .filter(Boolean);

        this.state = 'ready';
        this.filterProducts();

        if (!this.isMobile) {
          await this.$nextTick();
          this.constructCarousel();
        }
      } catch (error) {
        this.state = 'error';
        cnvs.ReportError('Failed to load cart samples', error);
      }
    },

    /**
     * Filter out variants already in cart or unavailable.
     */
    filterProducts() {
      if (this.state === 'loading') {
        this.filteredProducts = [false, false, false];
        return;
      }

      this.filteredProducts = this.products.filter((product) => {
        const variant = product.selectedOrFirstAvailableVariant;

        const variantInCart = this.cart.items.find((item) => {
          return item.variant_id === variant?.id;
        });

        return (
          variant?.available &&
          variant?.availableState !== 'hidden' &&
          !variantInCart
        );
      });

      this.filteredProducts = this.filteredProducts.slice(0, 8);

      this.overlay.refresh({ currentElement: document.activeElement });
    },

    /**
     * Handle resize event.
     */
    handleResize() {
      if (!this.isMobile) {
        this.constructCarousel();

        return;
      }

      if (this.isMobile && this.carousel) {
        this.carousel.destroy();
      }
    },

    /**
     * Handle error from cart sample.
     * @param {String} error - Error message.
     */
    handleError(error) {
      this.error = error;
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './cart-samples';
</style>
